import { h } from '@/lib/dom';
import { MarkerBadge } from '@/components/MarkerBadge';
import { PdfLink } from '@/components/PdfLink';
import { PinButton } from '@/components/PinButton';
import { Skeleton } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import { TrendChart } from '@/components/TrendChart';
import { toast } from '@/components/Toast';
import { data } from '@/lib/data';
import { labPdfUrl } from '@/api/labs';
import { flagToStatus, formatDate, formatDelta, formatLabValue, refWithFallback, statusLabel } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import type { EventOut, MarkerTrend, TrendPoint } from '@/api/types';

type Range = '3m' | '6m' | '1y' | 'all';

const RANGE_LABELS: Record<Range, string> = {
  '3m': '3м',
  '6m': '6м',
  '1y': '1г',
  all: 'Всё',
};

// Events from Ornament CSV import before this date are filtered out.
const EVENTS_FILTER_FROM = '2026-03-01';

function isoFrom(range: Range): string | undefined {
  if (range === 'all') return undefined;
  const now = new Date();
  const months = range === '3m' ? 3 : range === '6m' ? 6 : 12;
  const d = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
  return d.toISOString().slice(0, 10);
}

function filterByRange<T extends { started_at?: string; drawn_at?: string }>(
  items: T[],
  range: Range,
  dateKey: 'started_at' | 'drawn_at',
): T[] {
  if (range === 'all') return items;
  const from = isoFrom(range);
  if (!from) return items;
  const fromMs = new Date(from).getTime();
  return items.filter((i) => {
    const d = i[dateKey];
    if (!d) return false;
    return new Date(d).getTime() >= fromMs;
  });
}

// Collapses events for the same med on the same calendar day — the trend
// chart shows at most one flag per med per day. Every dose change is logged
// (and shown in full in the meds «История дозировок» disclosure), so a med
// can legitimately have several same-day med_change events; on the chart they
// collapse to one. Priority: med_start > med_change > med_stop. Falls back to
// (kind|title|day) for events without related_med_id.
const KIND_PRIORITY: Record<string, number> = { med_start: 0, med_change: 1, med_stop: 2 };

function dedupEvents(events: EventOut[]): EventOut[] {
  const byKey = new Map<string, EventOut>();
  for (const e of events) {
    const day = e.started_at.slice(0, 10);
    const key = e.related_med_id != null
      ? `med:${e.related_med_id}|${day}`
      : `t:${e.kind}|${e.title.trim()}|${day}`;
    const cur = byKey.get(key);
    if (!cur) {
      byKey.set(key, e);
      continue;
    }
    const curRank = KIND_PRIORITY[cur.kind] ?? 99;
    const newRank = KIND_PRIORITY[e.kind] ?? 99;
    if (newRank < curRank) byKey.set(key, e);
  }
  return [...byKey.values()].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
  );
}

export async function renderMarkerDetail(ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const markerKey = ctx.params['markerKey']!;
  let range: Range = 'all';
  let trend: MarkerTrend | null = null;
  let events: EventOut[] = [];
  let isPinned = false;
  let isPinLoading = false;

  const contentEl = h('div', { id: 'marker-content' },
    h('div', { class: 'flex flex-col gap-4' }, [
      Skeleton({ variant: 'rect', height: '48px' }),
      Skeleton({ variant: 'chart' }),
      Skeleton({ variant: 'rect', height: '200px' }),
    ]));

  const backLink = h('a', {
    href: '/labs',
    class: 'labs-back-link btn-ghost',
  }, '← Все анализы');

  const root = pageLayout({
    user,
    active: 'labs',
    children: [
      backLink,
      contentEl,
    ],
  });

  const renderContent = () => {
    if (!trend) return;
    const marker = trend.marker;
    const points = filterByRange(trend.points.slice(), range, 'drawn_at');

    // Sort points ascending for chart; last = most recent
    const sortedPoints = [...points].sort(
      (a, b) => new Date(a.drawn_at).getTime() - new Date(b.drawn_at).getTime(),
    );
    const lastPoint: TrendPoint | null = sortedPoints.length
      ? sortedPoints[sortedPoints.length - 1]!
      : null;
    const ariaState = lastPoint ? statusLabel(flagToStatus(lastPoint.flag)) : '';

    contentEl.innerHTML = '';

    const pinContainer = h('div', { class: 'pin-btn-container' });
    const renderPinBtn = () => {
      pinContainer.innerHTML = '';
      pinContainer.appendChild(PinButton({
        isPinned,
        isLoading: isPinLoading,
        onToggle: async () => {
          if (isPinLoading) return;
          const prev = isPinned;
          isPinLoading = true;
          renderPinBtn();
          try {
            const updated = await data.pinMarker(markerKey, !prev);
            isPinned = updated.is_priority;
            toast(isPinned ? 'Закреплено' : 'Откреплено', { type: isPinned ? 'success' : 'info' });
          } catch {
            isPinned = prev;
            toast('Ошибка при изменении приоритета', { type: 'error' });
          } finally {
            isPinLoading = false;
            renderPinBtn();
          }
        },
      }));
    };
    renderPinBtn();

    // Build reference string
    const refString = lastPoint && lastPoint.ref_low != null && lastPoint.ref_high != null
      ? (() => {
          const base = `Референс (последний): ${formatLabValue(lastPoint.ref_low)}–${formatLabValue(lastPoint.ref_high)} ${lastPoint.unit}`;
          const labPart = lastPoint.lab_name ? ` · ${lastPoint.lab_name}, ${formatDate(lastPoint.drawn_at)}` : ` · ${formatDate(lastPoint.drawn_at)}`;
          return base + labPart;
        })()
      : null;

    const header = h('section', { class: 'labs-marker-header' }, [
      h('div', { class: 'labs-marker-name-row' }, [
        h('h1', { class: 'labs-marker-name' }, marker.display_ru),
        pinContainer,
      ]),
      h('p', { class: 'labs-marker-meta' },
        `${marker.display_en} · ${marker.canonical_unit}`),
      h('div', { class: 'labs-marker-value-row' }, [
        h('span', { class: 'labs-marker-value tabular' },
          lastPoint
            ? (lastPoint.value != null
                ? `${formatLabValue(lastPoint.value)} ${lastPoint.unit}`
                : lastPoint.qualitative_value ?? '—')
            : '—'),
        lastPoint ? MarkerBadge({ flag: lastPoint.flag, withLabel: true, ariaLabelPrefix: marker.display_ru }) : null,
      ]),
      refString
        ? h('p', { class: 'labs-marker-ref' }, refString)
        : null,
    ]);

    const rangeToggle = h('div', {
      class: 'labs-range-switcher',
      role: 'tablist',
      aria: { label: 'Временной диапазон' },
    },
      (Object.keys(RANGE_LABELS) as Range[]).map((r) =>
        h('button', {
          class: `labs-range-btn${range === r ? ' active' : ''}`,
          type: 'button',
          role: 'tab',
          aria: { selected: range === r ? 'true' : 'false' },
          on: {
            click: () => {
              range = r;
              renderContent();
            },
          },
        }, RANGE_LABELS[r])));

    // Filter events: only those >= EVENTS_FILTER_FROM
    const filteredEvents = events.filter(
      (e) => e.started_at >= EVENTS_FILTER_FROM,
    );

    const chart = TrendChart({
      marker,
      points: sortedPoints,
      events: filterByRange(filteredEvents, range, 'started_at'),
      unit: marker.canonical_unit,
      referenceRange: lastPoint
        ? { low: lastPoint.ref_low, high: lastPoint.ref_high }
        : { low: marker.ref_low_default, high: marker.ref_high_default },
    });

    const refNote = h('p', { class: 'labs-ref-note' }, [
      h('span', { 'aria-hidden': 'true' }, 'ⓘ'),
      h('span', null, 'Полоса — референс последнего анализа. Флаги H/L у точек посчитаны по их собственным диапазонам — они могли отличаться от текущего.'),
    ]);

    // "Все значения" header with ⓘ flag info button
    const flagInfoBtn = h('button', {
      class: 'btn btn-ghost btn-sm',
      type: 'button',
      aria: { label: 'Как считается флаг H/L' },
      on: {
        click: () => {
          toast(
            'Флаг H/L присваивается по референсному диапазону лаборатории на момент конкретного забора. Разные заборы могут иметь разные диапазоны (разные лабы, разные реагенты).',
            { type: 'info', duration: 6000 },
          );
        },
      },
    }, 'ⓘ Как считается флаг H/L');

    const valuesHeader = h('div', { class: 'labs-values-header' }, [
      h('span', null, 'Все значения'),
      flagInfoBtn,
    ]);

    const sortedForTable = [...points].sort(
      (a, b) => new Date(b.drawn_at).getTime() - new Date(a.drawn_at).getTime(),
    );

    const valuesSection = h('div', { class: 'labs-values-section' }, [
      valuesHeader,
      sortedForTable.length
        ? h('div', { class: 'data-table-scroll' }, Table({
            columns: [
              { key: 'date', header: 'Дата', render: (row) => formatDate(row['date'] as string) },
              { key: 'value', header: 'Значение', align: 'right', render: (row) => {
                const v = row['value'] as number | null;
                const u = row['unit'] as string;
                return h('span', { class: 'tabular' },
                  v != null ? `${formatLabValue(v)} ${u}` : (row['qualitative_value'] as string) ?? '—');
              } },
              { key: 'delta', header: 'Δ', align: 'right', render: (row) => h('span', { class: 'tabular' }, (row['delta'] as string) ?? '') },
              { key: 'ref', header: 'Референс', align: 'right', render: (row) => {
                const cell = refWithFallback(
                  row['ref_low'] as number | null, row['ref_high'] as number | null,
                  marker.ref_low_default, marker.ref_high_default,
                );
                return cell.fromCatalog
                  ? h('span', { class: 'tabular text-tertiary', title: 'Из справочника — лаборатория не указала диапазон в этом отчёте' }, cell.text)
                  : h('span', { class: 'tabular' }, cell.text);
              } },
              { key: 'flag', header: 'Флаг', render: (row) => MarkerBadge({ flag: (row['flag'] as string) ?? null }) },
              { key: 'lab_name', header: 'Лаб.', render: (row) => {
                const ln = row['lab_name'] as string | null;
                return ln
                  ? h('span', null, ln)
                  : h('span', { class: 'text-tertiary' }, '—');
              } },
              { key: 'pdf', header: 'PDF', render: (row) =>
                row['has_pdf'] === false
                  ? h('span', { class: 'text-tertiary' }, '—')
                  : PdfLink({ href: labPdfUrl(row['lab_id'] as number), label: 'Открыть' }) },
            ],
            rows: pointsToRows(sortedForTable),
            ariaLabel: 'Все измерения маркёра',
          }))
        : h('div', { class: 'empty-state' }, 'Нет данных в выбранном диапазоне.'),
    ]);

    contentEl.append(
      header,
      rangeToggle,
      chart,
      refNote,
      valuesSection,
    );

    const liveRegion = h('div', {
      role: 'status',
      class: 'visually-hidden',
    }, `Диапазон: ${RANGE_LABELS[range]}. ${ariaState}.`);
    contentEl.appendChild(liveRegion);
  };

  void (async () => {
    try {
      trend = await data.getMarkerHistory(markerKey);
      isPinned = trend.marker.is_priority ?? false;
      const allEvents = await data.listEvents({ from: EVENTS_FILTER_FROM });
      events = dedupEvents(allEvents.filter((e) => e.started_at >= EVENTS_FILTER_FROM));
      renderContent();
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      contentEl.innerHTML = '';
      contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, 'Не удалось загрузить данные маркёра.'),
        h('button', {
          class: 'btn btn-secondary mt-2',
          type: 'button',
          on: { click: () => navigate(window.location.pathname, { replace: true }) },
        }, 'Повторить'),
      ]));
      console.error(err);
    }
  })();

  return root;
}

function pointsToRows(sorted: TrendPoint[]): Array<Record<string, unknown>> {
  return sorted.map((p, idx) => {
    const nextInList = sorted[idx + 1];
    const delta =
      nextInList && p.value != null && nextInList.value != null
        ? formatDelta(p.value - nextInList.value)
        : '';
    return {
      date: p.drawn_at,
      value: p.value,
      unit: p.unit,
      qualitative_value: p.qualitative_value,
      delta,
      ref_low: p.ref_low,
      ref_high: p.ref_high,
      flag: p.flag,
      lab_name: p.lab_name,
      lab_id: p.lab_id,
      has_pdf: p.has_pdf,
    };
  });
}

